/*
 * Sign in with Google.
 *
 * Uses Google Identity Services, loaded on demand rather than bundled - the
 * script must come from Google's origin to work, and there is no point pulling
 * it on a page load that is already signed in.
 *
 * A rejected address gets an explicit message. "Not permitted" reads as a
 * closed door; a blank failure reads as a broken app, and the mechanic would
 * have no idea which.
 */

import { useEffect, useRef, useState } from 'react'
import { Boxes } from 'lucide-react'
import { ApiError, api } from '../lib/api'
import type { User } from '../lib/api'

declare global {
  interface Window { google?: any }
}

export function Login({ onSignedIn }: { onSignedIn: (u: User) => void }) {
  const buttonRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [config, setConfig] = useState<{ configured: boolean; client_id: string } | null>(null)
  const [emailv, setEmailv] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const body = await api.post<{ user: User }>('/api/auth/login',
        { email: emailv.trim(), password })
      onSignedIn(body.user)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign in failed.')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    api.get<{ configured: boolean; client_id: string }>('/api/auth/config')
      .then(setConfig)
      .catch(() => setConfig({ configured: false, client_id: '' }))
  }, [])

  useEffect(() => {
    if (!config?.configured || !config.client_id) return
    const existing = document.getElementById('gis-script')
    if (existing) { setReady(true); return }
    const s = document.createElement('script')
    s.src = 'https://accounts.google.com/gsi/client'
    s.async = true
    s.id = 'gis-script'
    s.onload = () => setReady(true)
    s.onerror = () => setError('Could not reach Google to sign in.')
    document.head.appendChild(s)
  }, [config])

  useEffect(() => {
    if (!ready || !config?.client_id || !buttonRef.current || !window.google) return
    window.google.accounts.id.initialize({
      client_id: config.client_id,
      callback: async (response: { credential: string }) => {
        try {
          const body = await api.post<{ user: User }>('/api/auth/google',
            { credential: response.credential })
          onSignedIn(body.user)
        } catch (e) {
          setError(e instanceof ApiError ? e.message : 'Sign in failed.')
        }
      },
    })
    // filled_black, not filled_blue: the blue button renders on a light card
    // that stands out as a white box against this dark panel. The black theme
    // matches the card. The personalised variant (which forces a light
    // background) is suppressed by rendering the standard button explicitly.
    window.google.accounts.id.renderButton(buttonRef.current, {
      theme: 'filled_black', size: 'large', text: 'continue_with',
      shape: 'pill', width: 280,
    })
  }, [ready, config, onSignedIn])

  return (
    <div style={{
      minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 20,
    }}>
      <div className="card" style={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
        <div className="row" style={{ justifyContent: 'center', gap: 10, marginBottom: 6 }}>
          <Boxes size={26} color="#3aa0ff" />
          <div style={{ fontWeight: 750, fontSize: 20, letterSpacing: '-.02em' }}>
            GRAYSON <span style={{ color: 'var(--blue-2)' }}>KART</span>
          </div>
        </div>
        <p className="muted small" style={{ marginTop: 0, marginBottom: 22 }}>
          Setup, telemetry and video for a Mini ROK Cadet.
        </p>

        {config && !config.configured ? (
          <div className="advice warn" style={{ textAlign: 'left' }}>
            Google sign-in is not configured on this server yet
            (<code>GOOGLE_OAUTH_CLIENT_ID</code> and <code>APP_SESSION_SECRET</code>).
          </div>
        ) : (
          <>
            <div ref={buttonRef} style={{ display: 'flex', justifyContent: 'center' }} />

            <div className="row" style={{ gap: 10, alignItems: 'center',
              margin: '16px 0', color: 'var(--muted)' }}>
              <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
              <span className="tiny dim">or sign in with a password</span>
              <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
            </div>

            <form onSubmit={submitPassword} style={{ textAlign: 'left' }}>
              <label className="tiny dim" htmlFor="login-email">Username (email)</label>
              <input id="login-email" type="email" autoComplete="username"
                value={emailv} onChange={(e) => setEmailv(e.target.value)}
                placeholder="you@example.com" required
                style={{ width: '100%', marginBottom: 10 }} />
              <label className="tiny dim" htmlFor="login-pass">Password</label>
              <input id="login-pass" type="password" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                required style={{ width: '100%', marginBottom: 14 }} />
              <button className="btn" type="submit" disabled={busy}
                style={{ width: '100%', justifyContent: 'center' }}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </>
        )}

        {error && (
          <div className="advice warn" style={{ textAlign: 'left', marginTop: 16 }}>
            {error}
          </div>
        )}

        <p className="tiny dim" style={{ marginTop: 22, marginBottom: 0 }}>
          Access is limited to a set list of addresses.
        </p>
      </div>
    </div>
  )
}
